<template>
  <div class="page">
    <!-- 「開始」を押してから位置情報の判定が終わるまで（GPS の取得に時間がかかることがある）。判定のダイアログはこの上に出る -->
    <div v-if="starting" class="loading-mask">位置情報を確認しています…</div>

    <!-- スマホ前提なので実施画面と同じくヘッダー無し。上に緑のボタン（スクロールしても画面の上に固定） -->
    <div class="top-row">
      <PrimaryButton text="メニューへ" size="sm" class="go-btn bg-green-700 hover:bg-green-800" :buttonContents="'要'" @click="$router.push({ name: 'Launcher' })">
        <HomeIcon slot="before" class="w-5 h-5 mr-1" />
      </PrimaryButton>
      <PrimaryButton text="戻る" size="sm" class="go-btn bg-green-700 hover:bg-green-800" :buttonContents="'要'" @click="backToList()">
        <ArrowLeftIcon slot="before" class="w-5 h-5 mr-1" />
      </PrimaryButton>
      <!-- 右端：終了（開始前・終了後は出さない。終了はここからだけ。全箇所が完了したら目立つ色で点滅）。PDF は予定・結果から -->
      <template v-if="report && !loading && !notStarted">
        <PrimaryButton v-if="Number(report.status) < 3" text="終了" size="sm" class="go-btn ml-auto" :class="allDone ? 'finish-ready' : ''" :buttonContents="'要'" :disabled="saving || anyUploading" @click="complete()">
          <CheckCircleIcon slot="before" class="w-5 h-5 mr-1" />
        </PrimaryButton>
      </template>
    </div>

    <!-- 画面ごとスクロール -->
    <main class="bg-gray-50 form-main">
      <div v-if="loading" class="py-24 text-center font-bold text-gray-400">
        Loading...
      </div>

      <div v-else-if="!report" class="py-24 text-center font-bold text-gray-400">
        報告書が見つかりません。
      </div>

      <div v-else class="form-body">
        <!-- 物件 -->
        <div class="card">
          <div class="flex items-start gap-2">
            <div class="min-w-0 flex-1">
              <div class="text-lg font-bold text-green-900 leading-tight">{{ report.siteName }}</div>
              <div class="text-xs text-gray-500 mt-0.5">{{ report.clientName1 }}<span v-if="report.clientName2" class="ml-1">{{ report.clientName2 }}</span></div>
              <div class="text-xs text-gray-500">{{ report.siteAddress }}</div>
            </div>
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mt-2">
            <span>予定日 <b>{{ fmtDate(report.scheduledDate) }}</b></span>
            <span>担当 <b>{{ report.staffName || '未定' }}</b></span>
            <!-- 実施中は開始時間だけ、終了後は完了日と時間帯を 1 つに -->
            <span v-if="Number(report.status) >= 3" class="text-green-700 font-bold">完了 {{ fmtDate(report.completionDate) }} {{ report.startTime }}〜{{ report.endTime || '' }}</span>
            <span v-else-if="report.startTime">実施 <b>{{ report.startTime }}〜</b></span>
          </div>
        </div>

        <!-- 物件概要書の「鍵」「警報・警備」（建物に入る・警備を解除するときに見る。見るだけ）。見出しを押すと開閉。開始前は開いておく -->
        <div class="card outline-card">
          <button type="button" class="outline-head" :aria-expanded="outlineOpen" @click="outlineOpen = !outlineOpen">
            <KeyIcon class="w-5 h-5 text-amber-600" />
            <span class="font-bold text-base">鍵・警報・警備</span>
            <span class="text-xs text-gray-500 ml-1">（物件概要書）</span>
            <ChevronUpIcon v-if="outlineOpen" class="w-6 h-6 ml-auto text-gray-400" />
            <ChevronDownIcon v-else class="w-6 h-6 ml-auto text-gray-400" />
          </button>
          <div v-if="outlineOpen" class="outline-body">
            <div v-if="!siteOutline" class="text-sm text-gray-500">物件概要書の登録がありません。</div>
            <template v-else>
              <div v-for="g in outlineGroups" :key="g.name" class="outline-group">
                <div class="outline-group-name">{{ g.name }}</div>
                <div v-for="f in g.items" :key="f.key" class="outline-row">
                  <div class="outline-label">{{ f.name }}</div>
                  <!-- 有無：有は青、無も同じ太さ（「無」も大事な情報） -->
                  <div v-if="f.type === 'yesno'" class="outline-value">
                    <span v-if="siteOutline.outline[f.key] === true" class="yn yn-yes">有</span>
                    <span v-else-if="siteOutline.outline[f.key] === false" class="yn yn-no">無</span>
                    <span v-else class="text-gray-400">―</span>
                  </div>
                  <!-- 文字 -->
                  <div v-else-if="f.type === 'textarea'" class="outline-value outline-text">{{ (siteOutline.outline[f.key] || '').trim() || '―' }}</div>
                  <!-- 添付（備考の文字＋小さい写真。押すと大きく見る。写真以外は別タブで開く） -->
                  <div v-else-if="f.type === 'attach'" class="outline-value">
                    <div v-if="(siteOutline.outline[f.noteKey] || '').trim()" class="outline-text">{{ siteOutline.outline[f.noteKey] }}</div>
                    <div v-if="outlineAttach(f.key).length" class="outline-photos">
                      <template v-for="(a, i) in outlineAttach(f.key)">
                        <div v-if="isImageName(a.name)" :key="f.key + i" class="outline-thumb" @click="openOutlineViewer(f, i)">
                          <img :src="a.s3Url" loading="lazy" />
                        </div>
                        <a v-else :key="f.key + i" :href="a.s3Url" target="_blank" rel="noopener" class="outline-file">
                          <DocumentIcon class="w-5 h-5 flex-shrink-0" /><span class="truncate">{{ a.name }}</span>
                        </a>
                      </template>
                    </div>
                    <div v-if="!(siteOutline.outline[f.noteKey] || '').trim() && !outlineAttach(f.key).length" class="text-gray-400">―</div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 開始前は箇所を出さず、その場所に大きな「開始」（開始時間を記録。品質巡回と同じ） -->
        <div v-if="notStarted" class="card start-card">
          <!-- 色は PrimaryButton の既定（indigo）。緑は画面を移るボタンの色 -->
          <PrimaryButton text="開始" size="lg" class="start-btn" :buttonContents="'要'" :disabled="saving" @click="start()">
            <PlayIcon slot="before" class="w-7 h-7 mr-2" />
          </PrimaryButton>
        </div>

        <template v-else>
        <!-- 合計撮影枚数（契約の最低ラインの枚数。マスタ画面でだけ変えられる。1 箇所 1 枚なので箇所の数と比べ、終了は枚数以上が条件） -->
        <div class="card">
          <div class="flex items-center gap-3">
            <div class="flex-1">
              <div class="text-xs text-gray-500">合計撮影枚数</div>
              <div class="flex items-center gap-2 mt-1">
                <template v-if="report.totalPhoto">
                  <b class="text-xl">{{ report.totalPhoto }}</b>
                  <span class="text-sm text-gray-500">枚</span>
                </template>
                <span v-else class="text-sm font-bold text-red-600">未設定</span>
                <span class="ml-auto text-sm text-gray-700">撮影済み <b class="text-xl">{{ photoTotal }}</b> 枚</span>
              </div>
              <div class="text-xs text-gray-600 mt-1">指摘ありが多い時はこの枚数以上でも可能</div>
              <div v-if="!report.totalPhoto" class="text-xs font-bold text-red-600 mt-1">合計撮影枚数がマスタに未設定です（設定しないと終了できません）</div>
              <div class="text-xs font-bold text-red-600 mt-1">「お客様指定項目」と「合計撮影枚数」の設定はマスタ画面でのみ可能</div>
            </div>
          </div>
        </div>

        <!-- 完了した箇所は画面から消える。品質巡回と同じ「完了を再表示」で元の場所に写真ごと出す（あまり押されない前提） -->
        <div v-if="!locked && doneItems.length">
          <PrimaryButton v-if="!showDone" text="完了を再表示" size="lg" class="w-full justify-center redisplay-btn" @click="showDone = true" />
          <PrimaryButton v-else text="完了を非表示に戻す" size="lg" class="w-full justify-center redisplay-btn is-undo" @click="showDone = false" />
        </div>

        <!-- 並べ替え -->
        <div v-if="!locked && items.length" class="flex items-center justify-end px-1">
          <SecondaryButton text="並べ替え" size="sm" class="sort-btn" :disabled="saving || anyUploading" @click="openSort()">
            <SwitchVerticalIcon slot="before" class="w-5 h-5 mr-1" />
          </SecondaryButton>
        </div>

        <!-- 箇所（1 枚のカード＝1 箇所＝写真 1 枚。完了した箇所は下の「完了した箇所」へ。タブレット以上は 2 列） -->
        <div class="items">
        <!-- 完了した箇所（再表示中）も普通に直せて、「完了」は何度でも押せる -->
        <div v-for="it in openItems" :key="it.id" class="card item-card" :class="{ 'client-card': it.clientSpecified, 'issue-card': it.issue, 'done-card': it.done && !readonly }">
          <div class="flex items-center gap-2">
            <span class="no">{{ itemNo(it) }}</span>
            <!-- お客様指定（★）の行は箇所名を変えられない。それ以外は現地で変えてよい -->
            <template v-if="it.clientSpecified || locked">
              <span class="font-bold text-base flex-1 min-w-0 truncate">{{ it.item || '（箇所名なし）' }}</span>
            </template>
            <input
              v-else
              v-model="it.item"
              type="text"
              class="item-name flex-1 min-w-0 rounded border-gray-300 font-bold text-base py-1"
              :class="!it.item.trim() ? 'border-red-400 bg-red-50' : ''"
              placeholder="箇所名を入れてください"
              @blur="saveItem(it)"
            />
            <!-- お客様指定の札（付け外しはマスタ画面だけ。この画面では見るだけ） -->
            <span v-if="it.clientSpecified" class="tag"><StarIcon class="w-3 h-3" />お客様指定</span>
          </div>

          <!-- 写真：左が今回（1 箇所 1 枚・大きめ）、右が前回（同じアングルで撮るための見本・小さめ。前回の評価とコメントも見るだけで出す）。終了後は前回を出さない（品質巡回と同じ） -->
          <div class="pair mt-2" :class="readonly ? 'pair-single' : ''">
            <!-- 今回の写真（見出し無し。保存中は枠に「保存中…」をかぶせる） -->
            <div class="pair-col relative">
              <!-- 終了後は灰色の枠を付けず、写真をそのまま出す。押すと大きく見る -->
              <div v-if="it.photos.length && readonly" class="thumb thumb-plain" @click="openViewer(it, 0)">
                <img :src="it.photos[0].url" loading="lazy" />
              </div>
              <!-- 実施中は押すとそのまま撮り直し（スマホはカメラ起動。消すだけの操作は無い） -->
              <label v-else-if="it.photos.length" class="thumb thumb-retake" :class="it.uploading || locked ? 'pointer-events-none' : ''">
                <input type="file" accept="image/*" :capture="captureMode" class="hidden" :disabled="it.uploading || locked" @change="retakePhoto(it, $event)" />
                <img :src="it.photos[0].url" loading="lazy" />
              </label>
              <label v-else-if="!readonly" class="thumb add-thumb" :class="it.uploading ? 'pointer-events-none' : ''">
                <input type="file" accept="image/*" :capture="captureMode" class="hidden" :disabled="it.uploading" @change="pickPhoto(it, $event)" />
                <CameraIcon class="w-8 h-8" />
                <span class="text-sm font-bold">撮影</span>
              </label>
              <div v-else class="thumb thumb-none">写真なし</div>
              <div v-if="it.uploading" class="thumb-mask animate-pulse">保存中…</div>
            </div>
            <div v-if="!readonly" class="pair-col pair-prev">
              <div class="pair-label">前回</div>
              <div v-if="it.prevPhotos.length" class="thumb thumb-prev" @click="openPrevViewer(it, 0)">
                <img :src="it.prevPhotos[0].url" loading="lazy" />
              </div>
              <div v-else class="thumb thumb-none">前回なし</div>
            </div>
          </div>

          <!-- 前回の評価（見るだけ。今回には引き継がない＝毎回まっさらで始める）。ボタンのすぐ上に出して見落とさないように -->
          <div v-if="!readonly && prevButton(it)" class="prev-line mt-3">
            <span class="prev-label">前回</span>
            <span class="prev-eval" :class="prevButton(it).cls">{{ prevButton(it).name }}</span>
          </div>

          <!-- 評価は「指摘なし」「指摘あり」のどちらか 1 つ（毎回まっさらで始め、終了までに必ず押す）。同じボタンをもう一度押すと外す。指摘ありはコメント必須 -->
          <!-- 終了後は押したボタンだけ出す（PDF と同じ色。押していない古い報告書は PDF と同じく「指摘なし」扱い） -->
          <div v-if="readonly" class="evals-done mt-3">
            <template v-if="it.evals.length">
              <span v-for="e in onButtons(it)" :key="'e' + it.id + '-' + e.name" class="eval-btn is-on" :class="e.cls"><component :is="e.icon" class="w-5 h-5" />{{ e.name }}</span>
            </template>
            <span v-else class="eval-btn is-on ev-blue"><CheckCircleIcon class="w-5 h-5" />指摘なし</span>
          </div>
          <div v-else class="evals" :class="prevButton(it) ? 'mt-1' : 'mt-3'">
            <button
              v-for="e in evalButtons"
              :key="'e' + it.id + '-' + e.name"
              type="button"
              class="eval-btn"
              :class="[e.cls, it.evals.includes(e.name) ? 'is-on' : '']"
              :disabled="locked"
              @click="setEvaluation(it, e)"
            ><component :is="e.icon" class="w-5 h-5" />{{ e.name }}</button>
          </div>
          <!-- 終了後は枠なしの文字だけ（無ければ出さない） -->
          <div v-if="readonly && (it.comment || '').trim()" class="comment-plain mt-2">{{ it.comment }}</div>
          <template v-else-if="!readonly">
            <!-- 前回のコメント（見るだけ）。今回のコメント欄のすぐ上に -->
            <div v-if="(it.prevComment || '').trim()" class="prev-line mt-2">
              <span class="prev-label">前回</span>
              <span class="prev-comment">{{ it.prevComment }}</span>
            </div>
            <textarea
              v-model="it.comment"
              rows="2"
              class="w-full rounded border-gray-300 text-sm comment"
              :class="[needsComment(it) && !(it.comment || '').trim() ? 'border-red-400 bg-red-50' : (it.comment || '').trim() ? 'has-value' : '', (it.prevComment || '').trim() ? 'mt-1' : 'mt-2']"
              :placeholder="needsComment(it) ? 'コメント（必須）' : 'コメント'"
              :disabled="locked"
              @blur="saveItem(it)"
            ></textarea>
          </template>

          <!-- 左端：消す（★ 以外で写真が無い行だけ）。右端：この箇所の完了（箇所名・今回の写真・赤のボタンならコメント、がそろっていること）。全箇所が完了したら報告書の終了を聞く -->
          <div v-if="!locked" class="flex items-center justify-between mt-3">
            <button v-if="!it.clientSpecified && !it.photos.length" type="button" class="icon-btn" title="この箇所を消す" @click="deleteItem(it)">
              <TrashIcon class="w-6 h-6" />
            </button>
            <span v-else></span>
            <!-- 定期清掃の「完了」と同じ色（薄い青地に青の枠と文字）。完了した箇所（再表示中）でも何度でも押せる -->
            <PrimaryButton text="完了" size="sm" class="done-btn bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-800" :disabled="it.uploading || saving" @click="doneItem(it)" />
          </div>
        </div>
        </div>

        <!-- 現地で見つけた箇所を足す（終了したときにマスタにも入る） -->
        <div v-if="!locked">
          <SecondaryButton text="＋ 箇所を追加" size="lg" class="w-full justify-center add-item-btn" @click="addItem()" />
        </div>

        </template>

      </div>
    </main>

    <!-- 箇所の並べ替え（完了した箇所も含めて全部） -->
    <QualityReportSortModal v-model="sortShow" :open="sortOpen" :reportId="reportId" :items="items" @sorted="applySort" />

    <!-- 写真を大きく見る（終了後の今回の写真、前回の写真、物件概要書の写真。見るだけ。複数あれば ‹ › で送る） -->
    <div v-if="viewer.item" class="viewer" @click.self="viewer.item = null">
      <div class="viewer-head">
        <span class="font-bold">{{ viewer.item.item }}</span>
        <span class="text-sm ml-2">{{ viewer.label }}</span>
        <span v-if="viewer.photos.length > 1" class="text-sm ml-2">{{ viewer.index + 1 }} / {{ viewer.photos.length }}</span>
        <button type="button" class="ml-auto icon-btn text-white" @click="viewer.item = null"><XIcon class="w-8 h-8" /></button>
      </div>
      <img :src="viewer.photos[viewer.index].url" class="viewer-img" @click.stop />
      <div class="viewer-foot">
        <template v-if="viewer.photos.length > 1">
          <button type="button" class="viewer-btn" :disabled="viewer.index <= 0" @click="viewer.index--">‹ 前</button>
          <button type="button" class="viewer-btn" :disabled="viewer.index >= viewer.photos.length - 1" @click="viewer.index++">次 ›</button>
        </template>
        <button type="button" class="viewer-btn ml-auto" @click="viewer.item = null">閉じる</button>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from '@components/PrimaryButton.vue'
import SecondaryButton from '@components/SecondaryButton.vue'
import { StarIcon, TrashIcon, CameraIcon, XIcon, HomeIcon, ArrowLeftIcon, ExclamationIcon, CheckCircleIcon, SwitchVerticalIcon, PlayIcon, KeyIcon, ChevronDownIcon, ChevronUpIcon, DocumentIcon } from '@vue-hero-icons/solid'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as image from '@libs/image'
import * as utils from '@libs/utils'
import * as logManager from '@managers/logManager'
import moment from 'moment'
import PageModel from '../../main/pages/PageModel.vue'
import QualityReportSortModal from '../components/QualityReportSortModal.vue'

const DOW_NAME = ['日', '月', '火', '水', '木', '金', '土']
// 写真の長辺（px）。巡回・定期清掃の報告書と同じ 854（PDF の 3 列・指摘事項の 2 列でも足りる）
const PHOTO_MAX = 854

// 評価のボタン（「指摘なし」「指摘あり」のどちらか 1 つ。箇所の完了までに必ず押す）。押した name を保存する（evals は配列のまま。中身は 1 つ）
// issue=true は指摘あり扱い（PDF で赤）。needComment=true はコメント必須。色は PDF の札と同じ（指摘なし＝青・指摘あり＝赤）
const EVAL_BUTTONS = [
  { name: '指摘なし', issue: false, needComment: false, icon: 'CheckCircleIcon', cls: 'ev-blue' },
  { name: '指摘あり', issue: true, needComment: true, icon: 'ExclamationIcon', cls: 'ev-red' }
]

// 物件概要書から出す項目（鍵・警報・警備。物件概要書の画面 SiteOutlineEdit の定義と同じ並び）。key は tbl_site_outline の列（camelCase）、attach は添付の type＋備考の列
const OUTLINE_GROUPS = [
  { name: '鍵', items: [
    { key: 'isAutoLock', name: 'オートロック有無', type: 'yesno' },
    { key: 'autoLockNote', name: 'オートロック備考', type: 'textarea' },
    { key: 'keyBox', name: 'キーBOX番号', type: 'textarea' },
    { key: 'keyBoxAttach', name: 'キーBOX場所', type: 'attach', noteKey: 'keyBoxLocation' },
    { key: 'isHotelLock', name: 'ホテル錠有無', type: 'yesno' },
    { key: 'hotelLockNote', name: 'ホテル錠備考', type: 'textarea' }
  ] },
  { name: '警報・警備', items: [
    { key: 'isAlarm', name: '警報有無', type: 'yesno' },
    { key: 'alarmNote', name: '警報備考', type: 'textarea' },
    { key: 'alarmCancelAttach', name: '警備解除方法', type: 'attach', noteKey: 'alarmCancel' },
    { key: 'mgmtRoomAttach', name: '警備管理室', type: 'attach', noteKey: 'mgmtRoom' },
    { key: 'alarmCancelPanelAttach', name: '警備解除操作盤', type: 'attach', noteKey: 'alarmCancelPanel' },
    { key: 'isSecurityCamera', name: '防犯カメラ有無', type: 'yesno' }
  ] }
]

export default {
  extends: PageModel,
  components: {
    PrimaryButton,
    SecondaryButton,
    QualityReportSortModal,
    StarIcon,
    SwitchVerticalIcon,
    PlayIcon,
    TrashIcon,
    CameraIcon,
    XIcon,
    HomeIcon,
    ArrowLeftIcon,
    ExclamationIcon,
    CheckCircleIcon,
    KeyIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    DocumentIcon
  },

  data() {
    return {
      loading: false,
      saving: false,
      // 「開始」を押して位置情報を判定している間（画面にマスク）
      starting: false,
      // 報告書の頭（物件・状態）
      report: null,
      // 箇所（photos・uploading を最初から持たせる）
      items: [],
      // 評価のボタン
      evalButtons: EVAL_BUTTONS,
      // 物件概要書の鍵・警報・警備（{ outline, attach }。無ければ null）と、カードを開いているか
      siteOutline: null,
      outlineGroups: OUTLINE_GROUPS,
      outlineOpen: true,
      // 完了した箇所を再表示しているか（「完了を再表示」）
      showDone: false,
      // 並べ替えのモーダル（open は開くたびに増やす）
      sortShow: false,
      sortOpen: 0,
      // 写真を大きく見る（photos＝見ている写真の配列、label＝「今回」「前回」「物件概要書」）
      viewer: { item: null, photos: [], index: 0, label: '' }
    }
  },

  computed: {
    /**
     * 報告書 id（URL の ?id=）
     */
    reportId() {
      return Number(this.$route.query.id)
    },

    /**
     * 終了後は見るだけ
     */
    readonly() {
      return !this.report || Number(this.report.status) >= 3
    },

    /**
     * まだ「開始」を押していない（開始時間を取るので、押すまで撮影・入力はできない）
     */
    notStarted() {
      return !!this.report && Number(this.report.status) < 2
    },

    /**
     * 入力できない（終了後、または開始前）
     */
    locked() {
      return this.readonly || this.notStarted
    },

    /**
     * 写真の input の capture（スマホは背面カメラをそのまま起動。PC・タブレットは属性を付けずファイル選択）
     */
    captureMode() {
      return utils.deviceInfo.isMobile ? 'environment' : null
    },

    /**
     * 撮った枚数の合計
     */
    photoTotal() {
      return this.items.reduce((a, it) => a + it.photos.length, 0)
    },

    /**
     * 画面に出す箇所（完了していないもの。終了後と「完了を再表示」中は全部）
     */
    openItems() {
      return this.readonly || this.showDone ? this.items : this.items.filter((it) => !it.done)
    },

    /**
     * 完了した箇所
     */
    doneItems() {
      return this.items.filter((it) => it.done)
    },

    /**
     * 全部の箇所が完了していて、撮った枚数が合計撮影枚数に達している（右上の「終了」を点滅させる）
     */
    allDone() {
      return !this.locked && this.items.length > 0 && this.items.every((x) => x.done) && !!this.report.totalPhoto && this.photoTotal >= Number(this.report.totalPhoto)
    },

    /**
     * どこかの箇所で写真を保存中か
     */
    anyUploading() {
      return this.items.some((it) => it.uploading)
    },

  },

  async created() {
    await this.load()
  },

  methods: {
    /**
     * 一覧へ戻る
     */
    backToList() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'QualityReportMine' })
      }
    },

    /**
     * 日付の表示（M/D（曜））
     */
    fmtDate(d) {
      if (!d) {
        return '―'
      }
      const m = moment(d)
      return `${m.format('M/D')}（${DOW_NAME[m.day()]}）`
    },

    /**
     * 報告書を読む（初めて開いたときはバックがマスタの箇所を写す）
     * API: qualityReport/get
     */
    async load() {
      if (!this.reportId) {
        return
      }
      this.loading = true
      try {
        const res = await backend.searchData('qualityReport/get', { id: this.reportId, userId: this.$store.getters.user.id })
        const d = res.data.data
        this.report = d.report
        this.siteOutline = d.siteOutline || null
        // 鍵・警報・警備は建物に入るときに見るので開始前は開いておき、撮影が始まったら畳む（押せば開く）
        this.outlineOpen = Number(d.report.status) < 2
        const editing = Number(d.report.status) < 3
        this.items = (d.items || []).map((it) => {
          let evals = this.splitEvals(it.evaluation)
          let issue = !!it.issue
          // 実施中の報告書は、今のボタンに無い値（消したボタンの古い値）を捨てて、指摘ありの印も付け直す
          if (editing) {
            evals = evals.filter((n) => EVAL_BUTTONS.some((b) => b.name === n))
            issue = EVAL_BUTTONS.some((b) => b.issue && evals.includes(b.name))
          }
          return { ...it, item: it.item || '', evals, issue, comment: it.comment || '', done: !!it.done, photos: it.photos || [], prevPhotos: it.prevPhotos || [], prevEvaluation: it.prevEvaluation || '', prevComment: it.prevComment || '', uploading: false }
        })
      } catch (e) {
        console.log(e)
        this.report = null
        await dialogs.showErrorDialog('エラー', '報告書の取得に失敗しました。')
      }
      this.loading = false
    },

    /**
     * 箇所の番号（完了して画面から消えても番号は変えない）
     */
    itemNo(it) {
      return this.items.indexOf(it) + 1
    },

    /**
     * 箇所を完了にできないときの理由（null なら完了できる）
     */
    itemError(it) {
      if (!(it.item || '').trim()) {
        return '箇所名を入れてください。'
      }
      if (!it.photos.length) {
        return '今回の写真を撮ってください。'
      }
      // 今のボタン（指摘なし・指摘あり）のどちらかが押してあること（消したボタンの古い値は数えない）
      if (!this.onButtons(it).length) {
        return '「指摘なし」か「指摘あり」を押してください。'
      }
      if (this.needsComment(it) && !(it.comment || '').trim()) {
        return '「指摘あり」の箇所はコメントが必要です。'
      }
      return null
    },

    /**
     * 押してあるボタン（終了後の表示用。ボタンの並び順）
     */
    onButtons(it) {
      return EVAL_BUTTONS.filter((b) => it.evals.includes(b.name))
    },

    /**
     * 前回の報告書で押してあったボタン（前回の列に見るだけで出す。無ければ null）
     */
    prevButton(it) {
      const evals = this.splitEvals(it.prevEvaluation)
      return EVAL_BUTTONS.find((b) => evals.includes(b.name)) || null
    },

    /**
     * コメントが必須か（指摘ありを押している）
     */
    needsComment(it) {
      return EVAL_BUTTONS.some((b) => b.needComment && it.evals.includes(b.name))
    },

    /**
     * この箇所を完了（画面から消える）。全箇所が完了したら報告書の終了を聞く
     * API: qualityReport/doneItem
     */
    async doneItem(it) {
      const err = this.itemError(it)
      if (err) {
        await dialogs.showErrorDialog('完了できません', err)
        return
      }
      const wasDone = it.done
      this.saving = true
      try {
        await backend.postData('qualityReport/doneItem', { id: it.id, userId: this.$store.getters.user.id })
        it.done = true
        // 完了を再表示中に完了を押したら、完了を非表示の状態に戻す
        this.showDone = false
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '完了にできませんでした。')
        this.saving = false
        return
      }
      this.saving = false
      // 最後の箇所が完了して、撮った枚数が合計撮影枚数に達したら知らせるだけ（ここでは終了しない＝勢いで終了しないように。終了は右上のボタンからだけ）
      // 完了済みの箇所を直してもう一度「完了」を押したときは出さない
      if (!wasDone && this.allDone) {
        await dialogs.showInfoDialog('全部の箇所が完了しました', '撮り残しが無ければ、右上の「終了」を押してください。')
      }
    },

    /**
     * 箇所の保存（箇所名・評価・コメント。お客様指定はマスタ画面でだけ変える）
     * API: qualityReport/saveItem
     */
    async saveItem(it) {
      if (this.locked) {
        return
      }
      try {
        await backend.postData('qualityReport/saveItem', {
          id: it.id,
          item: it.item,
          evaluation: it.evals.join(','),
          issue: !!it.issue,
          comment: it.comment,
          userId: this.$store.getters.user.id
        })
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存失敗', 'エラー発生\r\n保存に失敗しました。')
      }
    },

    /**
     * 並べ替えのモーダルを開く
     */
    openSort() {
      this.sortOpen++
      this.sortShow = true
    },

    /**
     * 並べ替えの結果（id の並び）を画面の並びにする
     */
    applySort(ids) {
      const byId = {}
      this.items.forEach((it) => {
        byId[it.id] = it
      })
      const sorted = ids.map((id) => byId[id]).filter((it) => it)
      // 念のため、並びに無い行は末尾に
      this.items.forEach((it) => {
        if (!ids.includes(it.id)) {
          sorted.push(it)
        }
      })
      this.items = sorted
    },

    /**
     * 保存してある評価（カンマ区切り）→ 配列
     */
    splitEvals(s) {
      return (s || '').split(',').map((v) => v.trim()).filter((v) => v)
    },

    /**
     * 評価ボタン（どちらか 1 つ。もう一方を押すと入れ替わり、同じボタンをもう一度押すと外れる）
     */
    async setEvaluation(it, e) {
      it.evals = it.evals.includes(e.name) ? [] : [e.name]
      it.issue = EVAL_BUTTONS.some((b) => b.issue && it.evals.includes(b.name))
      await this.saveItem(it)
    },

    /**
     * 箇所を足す（現地で見つけた異常箇所など）
     * API: qualityReport/addItem
     */
    async addItem() {
      try {
        const res = await backend.postData('qualityReport/addItem', { reportId: this.reportId, item: '', userId: this.$store.getters.user.id })
        const row = res.data.data
        this.items.push({ ...row, item: row.item || '', evals: [], issue: false, comment: row.comment || '', done: false, photos: [], prevPhotos: [], prevEvaluation: '', prevComment: '', uploading: false })
        this.$nextTick(() => {
          const inputs = this.$el.querySelectorAll('.item-name')
          if (inputs.length) {
            inputs[inputs.length - 1].focus()
          }
        })
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '箇所を足せませんでした。')
      }
    },

    /**
     * 箇所を消す（写真の無い行だけ）
     * API: qualityReport/deleteItem
     */
    async deleteItem(it) {
      const r = await dialogs.showConfirmDialog('確認', `「${it.item || '（箇所名なし）'}」を消してよろしいですか？`, 'はい', 'キャンセル')
      if (r !== 'YES') {
        return
      }
      try {
        await backend.postData('qualityReport/deleteItem', { id: it.id })
        this.items = this.items.filter((x) => x.id !== it.id)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '消せませんでした。')
      }
    },

    /**
     * 撮った・選んだ写真を小さくして、その場で保存（1 箇所 1 枚）
     * API: qualityReport/savePhoto
     */
    async pickPhoto(it, event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length || this.locked || it.photos.length) {
        return
      }
      it.uploading = true
      try {
        const base64 = await this.compress(files[0])
        const res = await backend.postData('qualityReport/savePhoto', {
          reportId: this.reportId,
          itemId: it.id,
          base64,
          userId: this.$store.getters.user.id
        })
        const p = res.data.data
        it.photos.push({ id: p.id, path: p.path, url: p.url })
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存失敗', '写真を保存できませんでした。電波の良い所でもう一度お試しください。')
      }
      it.uploading = false
    },

    /**
     * 撮り直す（写真を押すとそのまま撮影。バックが新しい写真を保存してから古い写真を消す＝失敗しても古い写真が残る）
     * API: qualityReport/savePhoto
     */
    async retakePhoto(it, event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length || this.locked) {
        return
      }
      it.uploading = true
      try {
        const base64 = await this.compress(files[0])
        const res = await backend.postData('qualityReport/savePhoto', {
          reportId: this.reportId,
          itemId: it.id,
          base64,
          userId: this.$store.getters.user.id
        })
        const p = res.data.data
        it.photos.splice(0, it.photos.length, { id: p.id, path: p.path, url: p.url })
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存失敗', '写真を保存できませんでした。電波の良い所でもう一度お試しください。')
      }
      it.uploading = false
    },

    /**
     * File → 小さくした JPEG の data URL（撮影日を写真に描く）
     */
    compress(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(reader.error)
        reader.onload = () => {
          const img = new Image()
          img.onerror = () => reject(new Error('image load error'))
          img.onload = () => resolve(image.compressImage(img, false, PHOTO_MAX, 0.8))
          img.src = reader.result
        }
        reader.readAsDataURL(file)
      })
    },

    /**
     * 今回の写真を大きく見る（終了後だけ。実施中は押すと撮り直し）
     */
    openViewer(it, j) {
      this.viewer.item = it
      this.viewer.photos = it.photos
      this.viewer.index = j
      this.viewer.label = '今回'
    },

    /**
     * 前回の写真を大きく見る（見るだけ）
     */
    openPrevViewer(it, j) {
      this.viewer.item = it
      this.viewer.photos = it.prevPhotos
      this.viewer.index = j
      this.viewer.label = '前回'
    },

    /**
     * 物件概要書の添付（その項目の分。無ければ空）。システムに実体の無い分（サイボウズ時代・URL なし）は出さない
     */
    outlineAttach(key) {
      const list = (this.siteOutline && this.siteOutline.attach && this.siteOutline.attach[key]) || []
      return list.filter((a) => a.s3Url)
    },

    /**
     * ファイル名が画像か（物件概要書の画面と同じ判定）
     */
    isImageName(name) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '')
    },

    /**
     * 物件概要書の写真を大きく見る（その項目の画像だけを ‹ › で送れる）
     * @param {*} f 項目（OUTLINE_GROUPS の items）
     * @param {Number} i 押した添付の添え字（画像以外も含む並びの中の）
     */
    openOutlineViewer(f, i) {
      const all = this.outlineAttach(f.key)
      const images = all.filter((a) => this.isImageName(a.name))
      if (!images.length) {
        return
      }
      const picked = all[i]
      this.viewer.item = { item: f.name }
      this.viewer.photos = images.map((a) => ({ url: a.s3Url }))
      this.viewer.index = Math.max(0, images.indexOf(picked))
      this.viewer.label = '物件概要書'
    },

    /**
     * 開始（現在地と物件の距離を比べ、通れば開始時間を記録して実施中にする）
     * API: qualityReport/start
     */
    async start() {
      this.saving = true
      this.starting = true
      try {
        const ok = await this.checkGps()
        if (!ok) {
          return
        }
        await backend.postData('qualityReport/start', { id: this.reportId, userId: this.$store.getters.user.id })
        this.report.status = 2
        this.report.startTime = moment().format('HH:mm')
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '開始できませんでした。')
      } finally {
        this.starting = false
        this.saving = false
      }
    },

    /**
     * 開始時の GPS の判定（品質巡回と同じ判定。現在地は緯度経度だけで住所には直さない）
     * ・GPS が取れない／許容範囲（mst_param gps_diff5。無ければ 0.003 ≒ 300 m）より離れていると止める。管理者は解除して通す
     * ・離れていたら物件の緯度経度を住所から取り直し、それでも離れていれば現在地を 3 回取り直す
     * ・止めた・解除したときだけ理由を報告書の行（gps_note）に残す。住所の無い物件は比べない
     * API: patrolSchedule/getGpsDiff・patrolSchedule/updatePosition・qualityReport/saveGpsNote
     * @returns 開始してよければ true
     */
    async checkGps() {
      const r = this.report
      if (!r.siteAddress) {
        return true
      }
      const auth = this.$store.getters.auth
      const isAdmin = !!(auth && auth.adminAuth)
      const cannot = 'そのため、報告書が作成できません。'
      // 前に止めた記録が残っていれば消す（通ったときは何も残さない。管理者の解除はこのあと入れ直す）
      if (r.gpsNote) {
        await this.saveGpsNote('')
      }

      // 許容範囲
      let gpsDiff = 0.003
      try {
        const res = await backend.searchData('patrolSchedule/getGpsDiff', { type: 5 })
        if (res.data && Number(res.data.gpsDiff)) {
          gpsDiff = Number(res.data.gpsDiff)
        }
      } catch (e) {
        console.log(e)
      }
      const inRange = (cur) => cur && cur.lat && r.latitude && r.longitude && Math.abs(Number(r.latitude) - cur.lat) <= gpsDiff && Math.abs(Number(r.longitude) - cur.lng) <= gpsDiff

      // 現在地
      let cur = await utils.getLocation2()
      if (!cur || !cur.lat) {
        const err = '現在の位置情報（GPS）が取得できません。'
        if (isAdmin) {
          await this.saveGpsNote(`${err}（管理者が解除）`)
          await dialogs.showInfoDialog('GPS', 'GPSがOFFですが管理者のため解除します。\r\n解除したことはデータに保存されています。')
          return true
        }
        await this.saveGpsNote(err)
        await dialogs.showErrorDialog('GPS', `${err}\r\n${cannot}\r\n【GPS取得できない原因】\r\n・現在地を取得しない設定にしている。\r\n・GPS電波の悪い場所にいる。\r\n・省電力モードにしている。\r\n※GPS許可した場合は『キャンセル』して、再度試してください。`)
        return false
      }

      // 物件の緯度経度が無ければ住所から取る（取れなければ止める。管理者は解除して通す）
      if (!r.latitude || !r.longitude) {
        if (!(await this.updateSitePosition())) {
          const err = '物件の住所から緯度経度が取得できませんでした。'
          if (isAdmin) {
            await this.saveGpsNote(`${err}（管理者が解除）`)
            await dialogs.showInfoDialog('GPS', '物件の位置が取れませんが管理者のため解除します。\r\n解除したことはデータに保存されています。')
            return true
          }
          await this.saveGpsNote(err)
          await dialogs.showErrorDialog('GPS', `${err}\r\n${cannot}\r\n${r.siteName} の住所が正しいかご確認ください。\r\n●登録されている住所：${r.siteAddress}`)
          return false
        }
      }
      if (inRange(cur)) {
        return true
      }

      // 離れている：物件の緯度経度を取り直し（住所が変わっていた場合に備えて）、それでも離れていれば現在地を取り直す
      await this.updateSitePosition()
      if (inRange(cur)) {
        return true
      }
      for (let i = 0; i < 3; i++) {
        cur = await utils.getLocation2()
        if (inRange(cur)) {
          return true
        }
      }

      const range = Math.round(gpsDiff * 100000)
      const err = `現在地と対象物件が${range}ｍ以上離れています。`
      if (isAdmin) {
        await this.saveGpsNote(`${err}（管理者が解除）`)
        await dialogs.showInfoDialog('GPS', '物件から離れていますが管理者のため解除します。\r\n解除したことはデータに保存されています。')
        return true
      }
      await this.saveGpsNote(err)
      await dialogs.showErrorDialog('GPS', `${err}\r\n${cannot}\r\n登録されている物件住所が正しいかご確認ください。\r\n●登録住所：${r.siteAddress}`)
      return false
    },

    /**
     * 物件の緯度経度を住所から取り直して保存（tbl_site_position_coord）
     * API: patrolSchedule/updatePosition
     * @returns 取れたら true
     */
    async updateSitePosition() {
      try {
        const res = await backend.searchData('patrolSchedule/updatePosition', { siteCode: this.report.siteCode, address: this.report.siteAddress })
        const pos = res.data && res.data.data
        if (pos && pos.lat && pos.lng) {
          this.report.latitude = pos.lat
          this.report.longitude = pos.lng
          return true
        }
      } catch (e) {
        console.log(e)
      }
      return false
    },

    /**
     * GPS の判定で止めた・解除した理由を報告書の行に残す（画面にも出す）
     * API: qualityReport/saveGpsNote
     */
    async saveGpsNote(note) {
      this.report.gpsNote = note || null
      try {
        await backend.postData('qualityReport/saveGpsNote', { id: this.reportId, note, userId: this.$store.getters.user.id })
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 終了（合計撮影枚数がマスタに入っていて、全部の箇所が完了していて、箇所の数が合計撮影枚数以上であること。枚数は最低ラインで、多い分はサービス）
     * 終了するとマスタに反映される（箇所・担当者・実施日・初回実施日）
     * API: qualityReport/complete
     */
    async complete() {
      if (!this.report.totalPhoto) {
        await dialogs.showErrorDialog('終了できません', '合計撮影枚数がマスタに設定されていません。\r\nマスタ画面で設定してください。')
        return
      }
      if (!this.items.length) {
        await dialogs.showErrorDialog('終了できません', '箇所がありません。')
        return
      }
      const notDone = this.items.filter((it) => !it.done)
      if (notDone.length) {
        await dialogs.showErrorDialog('終了できません', `完了していない箇所があります。\r\n${notDone.map((it) => '・' + (it.item || '（箇所名なし）')).join('\r\n')}`)
        return
      }
      // 1 箇所 1 枚なので、箇所の数（＝写真の数）が合計撮影枚数（最低ライン）に足りなければ終了できない（多い分はよい）
      if (this.items.length < Number(this.report.totalPhoto)) {
        await dialogs.showErrorDialog('終了できません', `合計撮影枚数（${this.report.totalPhoto} 枚）に足りません。`)
        return
      }
      const r = await dialogs.showConfirmDialog('終了', '終了してよろしいですか？\r\n終了するとこの画面では直せなくなります。', '終了する', 'キャンセル')
      if (r !== 'YES') {
        return
      }
      this.saving = true
      try {
        await backend.postData('qualityReport/complete', { id: this.reportId, userId: this.$store.getters.user.id })
        this.report.status = 3
        this.report.completionDate = moment().format('YYYY-MM-DD')
        this.report.endTime = moment().format('HH:mm')
        if (!this.report.startTime) {
          this.report.startTime = this.report.endTime
        }
        logManager.recording(this, logManager.Loglevel.INFO, 'クオリティレポート', '報告書', `物件名：${this.report.siteName} の報告書を終了しました（写真 ${this.photoTotal} 枚）。`)
        // 終了したらすぐ一覧へ戻る
        this.saving = false
        this.backToList()
        return
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '終了できませんでした。')
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
/* 下の固定バーの分（＋ iPhone の下の余白）を空ける */
/* PC で横に伸びすぎないよう幅を抑えて中央に（実施画面と同じ） */
.page {
  max-width: 820px;
  margin: 0 auto;
}

/* 上の緑のボタンの行（スクロールしても画面の上に固定） */
.top-row {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  margin-bottom: 4px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

/* 同じ種類のボタンは同じ幅。小さいスマホ（320px）でも 3 つが 1 行に入る幅 */
.go-btn {
  width: 6rem;
  height: 36px;
  padding: 0 4px !important;
  justify-content: center;
  white-space: nowrap;
  font-size: 13px;
}

.go-btn svg {
  width: 16px !important;
  height: 16px !important;
}

/* 全部の箇所が完了したら「終了」を目立つ色（赤）で点滅させる。Tailwind の色が important でアニメーションでは色を変えられないので、点滅は透明度と光る輪で */
.finish-ready {
  background-color: #dc2626 !important;
  animation: finish-blink 1s ease-in-out infinite;
}

@keyframes finish-blink {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
  }
  50% {
    opacity: 0.45;
    box-shadow: 0 0 0 8px rgba(220, 38, 38, 0);
  }
}

.form-main {
  min-height: calc(100vh - 3.5rem);
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
}

/* 今回（左・大きめ）と前回（右・小さめ）の写真を左右に。前回は写真の下に前回の評価とコメント */
.pair {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 10px;
  align-items: start;
}

/* 終了後：今回の写真だけを真ん中に（大きさは実施中の今回と同じ 3/5 幅） */
.pair-single {
  grid-template-columns: minmax(0, 60%);
  justify-content: center;
}

.pair-prev {
  min-width: 0;
}

/* 前回の評価・コメント（見るだけ）。今回のボタン・コメント欄のすぐ上に「前回」の札付きで 1 行 */
.prev-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.prev-label {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 11px;
  font-weight: bold;
  line-height: 18px;
}

.prev-eval {
  display: inline-block;
  padding: 1px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
}

.prev-eval.ev-blue {
  background: #2563eb;
}

.prev-eval.ev-red {
  background: #dc2626;
}

.prev-comment {
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #6b7280;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.pair-label {
  font-size: 12px;
  font-weight: bold;
  color: #6b7280;
  margin-bottom: 4px;
}

/* 保存中は写真の枠にかぶせる */
.thumb-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  color: #1d4ed8;
  font-weight: bold;
  font-size: 15px;
}

/* 前回の写真は見本なので少し薄く */
.thumb-prev {
  opacity: 0.85;
  border-style: dashed;
}

.thumb-none {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
  cursor: default;
}

.done-btn {
  min-height: 44px;
  min-width: 10rem;
  justify-content: center;
}

.sort-btn {
  min-height: 44px;
}

/* 「完了を再表示」（品質巡回と同じ見た目：薄い青緑に青緑の枠と文字。戻すのは薄い黄色） */
.redisplay-btn {
  min-height: 44px;
  background: #f0fdfa;
  color: #0f766e;
  border: 1px solid #0f766e;
}

.redisplay-btn:hover {
  background: #ccfbf1;
}

.redisplay-btn.is-undo {
  background: #fefce8;
  color: #a16207;
  border-color: #ca8a04;
}

.redisplay-btn.is-undo:hover {
  background: #fef9c3;
}

/* 再表示した完了の箇所（緑の枠） */
.done-card {
  border-color: #6ee7b7;
}

.form-body {
  max-width: 820px;
  margin: 0 auto;
  padding: 12px 12px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 箇所のカードはタブレット以上で 2 列。minmax(0, …) で中身の幅に引っ張られず、上の物件の枠と同じ幅にそろえる */
.items {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  /* 2 列のとき、隣のカードに合わせて引き伸ばさない（「完了」の下に余白が残らない） */
  align-items: start;
}

.item-card {
  min-width: 0;
}

@media screen and (min-width: 768px) {
  .items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
}

/* 物件概要書の鍵・警報・警備（見出しを押して開閉。枠は黄色） */
.outline-card {
  padding: 0;
  overflow: hidden;
  border: 2px solid #f59e0b;
}

.outline-head {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 48px;
  padding: 8px 14px;
  text-align: left;
  background: #fffbeb;
}

.outline-body {
  padding: 6px 14px 12px;
  border-top: 1px solid #fde68a;
}

.outline-group + .outline-group {
  margin-top: 10px;
}

.outline-group-name {
  font-size: 12px;
  font-weight: bold;
  color: #b45309;
  padding: 6px 0 2px;
  border-bottom: 1px solid #fde68a;
  margin-bottom: 4px;
}

/* 1 項目＝見出し（小さく灰色）と値。スマホは縦に、広い画面は横に */
.outline-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2px;
  padding: 6px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.outline-row:last-child {
  border-bottom: none;
}

@media screen and (min-width: 480px) {
  .outline-row {
    grid-template-columns: 9rem minmax(0, 1fr);
    gap: 10px;
    align-items: start;
  }
}

.outline-label {
  font-size: 12px;
  color: #6b7280;
}

.outline-value {
  font-size: 15px;
  color: #1f2937;
  min-width: 0;
}

/* 文字は改行をそのまま */
.outline-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

/* 有無（「無」も大事な情報なので同じ太さ・同じ大きさで色を付ける。有＝青、無＝灰色の塗り。未登録は「―」） */
.yn {
  display: inline-block;
  min-width: 3rem;
  text-align: center;
  padding: 2px 10px;
  border-radius: 6px;
  font-weight: bold;
}

.yn-yes {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.yn-no {
  background: #e5e7eb;
  color: #374151;
  border: 1px solid #9ca3af;
}

/* 添付の小さい写真（押すと大きく見る）とファイル */
.outline-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.outline-thumb {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
  cursor: pointer;
}

.outline-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.outline-file {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-height: 36px;
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #1d4ed8;
  background: #fff;
}

/* 開始前：箇所の代わりに大きな「開始」 */
.start-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 14px;
}

.start-btn {
  width: 100%;
  max-width: 360px;
  min-height: 64px;
  justify-content: center;
  font-size: 22px;
}

/* 入力欄は 16px 以上（iOS で勝手に拡大されない）・押しやすい高さ */
.item-name,
.comment {
  font-size: 16px;
}

.item-name {
  min-height: 44px;
}

/* 丸いアイコンのボタン（44px の押し場所） */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  color: #9ca3af;
  flex-shrink: 0;
}

.icon-btn:hover {
  background: #f3f4f6;
  color: #dc2626;
}

.add-item-btn {
  min-height: 44px;
}

/* お客様ご指定の箇所 */
.client-card {
  border-color: #6ee7b7;
  background: #f0fdf4;
}

/* 指摘ありの箇所 */
.issue-card {
  border-color: #fca5a5;
}

.no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  background: #065f46;
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  flex-shrink: 0;
}


.tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  background: #d1fae5;
  color: #047857;
  white-space: nowrap;
}

/* 切り替え（同じ幅・押しやすい高さ） */
/* 写真の枠（正方形） */
.thumb {
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
  cursor: pointer;
}

/* 実施中の写真は label（押すと撮り直し）。inline のままだと枠の高さが出ない（「撮影」の枠 .add-thumb は flex のまま） */
.thumb-retake {
  display: block;
}

/* 写真は縦横の比をそのまま（切らずに枠の中に収める） */
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 終了後：枠なし・写真の比のまま（正方形にしない） */
.thumb.thumb-plain {
  aspect-ratio: auto;
  border: none;
  background: none;
}

.thumb.thumb-plain img {
  height: auto;
  display: block;
  border-radius: 8px;
}

.add-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 2px dashed #059669;
  background: #fff;
  color: #047857;
}

/* 評価のボタン（指摘なし・指摘ありの 2 つを同じ幅で横に。押していないときは白、押すとその色） */
.evals {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.eval-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  padding: 6px 4px;
  font-size: 15px;
  font-weight: bold;
  white-space: nowrap;
}

.eval-btn:disabled {
  opacity: 0.7;
}

/* 小さいスマホ（320px）では文字とアイコンを少し小さく（折り返さない） */
@media screen and (max-width: 359px) {
  .evals {
    gap: 6px;
  }

  .eval-btn {
    font-size: 13px;
    gap: 2px;
    padding: 6px 2px;
  }

  .eval-btn svg {
    width: 16px !important;
    height: 16px !important;
  }
}

/* 終了後：押したボタンだけ出す（ボタン 1 段分の高さにして、コメント欄の位置をそろえる） */
.evals-done {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 36px;
  align-items: center;
}

.evals-done .eval-btn {
  min-height: 36px;
  padding: 4px 12px;
}

/* 押していないときは文字だけその色（どちらが何色か分かる）。押すと濃い色に白文字（薄い色だと必須欄の赤枠と見分けがつかない）。PDF の札と同じ色：指摘なし＝青、指摘あり＝赤 */
.eval-btn.ev-blue {
  color: #2563eb;
  border-color: #93c5fd;
}

.eval-btn.ev-red {
  color: #dc2626;
  border-color: #fca5a5;
}

.eval-btn.is-on {
  color: #fff;
}

.eval-btn.ev-blue.is-on {
  background: #2563eb;
  border-color: #2563eb;
}

.eval-btn.ev-red.is-on {
  background: #dc2626;
  border-color: #dc2626;
}

.comment {
  field-sizing: content;
  min-height: 2.5rem;
}

/* コメントが書いてある欄は黄色（検索欄と同じ決まり。必須で空のときは赤枠のまま） */
.comment.has-value {
  background: #fef9c3 !important;
  border-color: #ca8a04 !important;
  color: #1f2937;
  font-weight: 600;
}

/* 終了後のコメント（枠なし・改行はそのまま） */
.comment-plain {
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* 下の固定バー */
/* 写真を大きく見る */
.viewer {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 50;
  display: flex;
  flex-direction: column;
  color: #fff;
}

.viewer-head {
  display: flex;
  align-items: center;
  padding: 10px 14px;
}

.viewer-img {
  flex: 1;
  min-height: 0;
  object-fit: contain;
  width: 100%;
}

.viewer-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
}

.viewer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 48px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #fff;
  font-size: 16px;
}

.viewer-btn:disabled {
  opacity: 0.3;
}

.viewer-btn.danger {
  border-color: #f87171;
  color: #fca5a5;
}
</style>
